import { Children, isValidElement, type ReactNode } from "react";
import { KarteLink, PFEIL } from "./KarteLink";
import ui from "./ui.module.css";
import k from "./kachel.module.css";

/* Kachel (BAUPLAN-FEINSCHLIFF.md, Abschnitt 3.6, ADR-021): ein Aufbau für alle Kopfkacheln (Meine Woche, Übersicht einer Kapsel,
   Budget). Teile in fester Reihenfolge: Oberzeile, Hauptzeile, Stand, Zusatz, Balken oder freier Platz in Balkenhöhe, Fuß. Der Platz
   hält Titel und Fuß nebeneinanderstehender Kacheln auf einer Linie. Immer weiß mit Rand und Schatten, Farbe nur als Band oben.
   Mit `link` ist die ganze Kachel ein Link (KarteLink), der Fuß nur Text. Ohne `link` gibt es keinen Fuß. */

export interface BalkenTeil {
  /** 0 bis 100 */
  anteil: number;
  /** CSS-Farbe, zum Beispiel "var(--black)" */
  farbe: string;
}

interface Props {
  /** „Sprint · noch 10 Tage“ */
  ober: ReactNode;
  /** Punkt vor der Oberzeile, zum Beispiel in der Farbe eines Vorhabens */
  punkt?: string | undefined;
  /** Zeichen vor der Oberzeile, zum Beispiel ein Pfeil bei Einnahmen */
  zeichen?: ReactNode;
  /** Name, Datum oder Betrag */
  haupt: ReactNode;
  /** Hauptzeile als große Zahl (28 px, 800) */
  zahl?: boolean | undefined;
  farbe?: "ein" | "aus" | undefined;
  /** Eine Zeile Stand, Zahlen darin mit <b> */
  stand?: ReactNode;
  zusatz?: ReactNode;
  /** Ohne Teile: freier Platz in Balkenhöhe */
  balken?: readonly BalkenTeil[] | undefined;
  /** Farbe der Spur unter dem Balken, Vorgabe --surface */
  spur?: string | undefined;
  band?: "ein" | "aus" | "schwarz" | undefined;
  /** Ganze Kachel als Link: Ziel, Name für Screenreader, Text im Fuß („Zum Sprint“) */
  link?: { to: string; label: string; weiter: string } | undefined;
}

export function Kachel({ ober, punkt, zeichen, haupt, zahl, farbe, stand, zusatz, balken, spur, band, link }: Props) {
  const klassen = [k.kachel, band && k.band, band && k[`band_${band}`]].filter(Boolean).join(" ");
  const inhalt = (
    <>
      <p className={k.ober}>
        {punkt ? <span className={k.punkt} style={{ background: punkt }} aria-hidden="true" /> : null}
        {zeichen}
        <span>{ober}</span>
      </p>
      <p className={[k.haupt, zahl && k.zahl, farbe && k[farbe]].filter(Boolean).join(" ")}>{haupt}</p>
      {stand ? <p className={k.stand}>{stand}</p> : null}
      {zusatz ? <p className={k.zusatz}>{zusatz}</p> : null}
      {balken?.length ? (
        <div className={k.balken} style={spur ? { background: spur } : undefined} aria-hidden="true" data-teil="balken">
          {balken.map((b, i) => (
            <span key={i} style={{ width: `${Math.max(0, Math.min(100, b.anteil))}%`, background: b.farbe }} />
          ))}
        </div>
      ) : (
        <div className={[k.balken, k.platz].join(" ")} aria-hidden="true" data-teil="platz" />
      )}
    </>
  );
  if (link) {
    return (
      <KarteLink to={link.to} label={link.label} className={klassen}>
        {inhalt}
        <span className={k.fuss}>
          <span className={ui.karteWeiter}>
            {link.weiter} {PFEIL}
          </span>
        </span>
      </KarteLink>
    );
  }
  return <div className={[ui.karte, klassen].join(" ")}>{inhalt}</div>;
}

/** Raster für Kacheln: gleich breite Spalten ab 220 px, alle Kacheln einer Reihe gleich hoch, am Handy eine Spalte. `drei`: drei gleiche
 *  Spalten (Budget). */
export function Kacheln({ label, drei, children }: { label?: string | undefined; drei?: boolean | undefined; children: ReactNode }) {
  return (
    <ul className={[k.kacheln, drei && k.drei].filter(Boolean).join(" ")} aria-label={label}>
      {Children.toArray(children).map((kind, i) => (
        <li key={isValidElement(kind) && kind.key !== null ? kind.key : i}>{kind}</li>
      ))}
    </ul>
  );
}
